import React from 'react';

// Top-level error boundary so a render-time exception doesn't leave the user staring
// at a white screen (which is exactly what happened with the recent TDZ bug).
//
// Catches:
//  - Render errors in any descendant component
//  - Lifecycle errors (componentDidMount, etc.)
// Does NOT catch:
//  - Async errors inside event handlers / promises (use try/catch there)
//  - Errors thrown inside the boundary's own render
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface to the console; in production this would also POST to a logging endpoint.
    console.error('[Orbit] Uncaught render error:', error);
    if (info?.componentStack) console.error('[Orbit] Component stack:', info.componentStack);
    this.setState({ info });
  }

  handleReload = () => { try { window.location.reload(); } catch {} };
  handleGoHome = () => { try { window.location.assign('/'); } catch {} };
  handleCopy = () => {
    const txt = [
      'Orbit crash report',
      `URL: ${window.location.href}`,
      `When: ${new Date().toISOString()}`,
      `Error: ${this.state.error?.message || this.state.error}`,
      this.state.error?.stack ? `Stack:\n${this.state.error.stack}` : '',
      this.state.info?.componentStack ? `Component stack:${this.state.info.componentStack}` : '',
    ].filter(Boolean).join('\n\n');
    try { navigator.clipboard.writeText(txt); } catch {}
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const err = this.state.error;
    const msg = err?.message || String(err) || 'Unknown error';

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-lg w-full bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-red-500 via-rose-500 to-orange-500" />
          <div className="px-6 sm:px-8 py-7">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center text-xl">⚠️</div>
              <div className="flex-1">
                <h1 className="text-lg font-bold text-gray-900">Something went wrong</h1>
                <p className="text-sm text-gray-600 mt-1">Orbit ran into an unexpected error while rendering. Your work hasn't been lost — refreshing will reload the latest state.</p>
              </div>
            </div>
            <details className="mt-4 mb-5">
              <summary className="text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700">Technical details</summary>
              <pre className="mt-2 text-[11px] bg-gray-50 border border-gray-200 rounded-lg p-3 text-gray-700 max-h-48 overflow-auto whitespace-pre-wrap leading-relaxed">{msg}{err?.stack ? `\n\n${err.stack}` : ''}</pre>
            </details>
            <div className="flex flex-wrap gap-2">
              <button onClick={this.handleReload}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium shadow-sm">
                Reload page
              </button>
              <button onClick={this.handleGoHome}
                className="px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
                Go to dashboard
              </button>
              <button onClick={this.handleCopy}
                className="px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
                Copy error details
              </button>
            </div>
          </div>
          <div className="px-6 sm:px-8 py-3 bg-gray-50/60 border-t border-gray-100 text-[11px] text-gray-500">
            If this keeps happening, paste the error details to your admin.
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
